import React, { Component } from 'react';
import { Form, Input, Progress } from 'antd';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import styled from '@emotion/styled';
import { ProjectsActions } from 'redux/actions';
import {
  PrimaryButton,
  formLayout,
  formValidateMessages,
  FormWrapper,
} from 'utils';
import { UploadImageV2 } from 'components';
import { projectsServices } from 'services';

const FormItem = Form.Item;

const MAX_IMAGES = 5;

const inputShadow = `0px 1px 2px rgba(10, 13, 18, 0.05)`;

// Gleicher Aufbau wie das "Goodie hinzufuegen"-Fenster im Projektformular
// (ProjectFormV2/components/RewardsModal).
const StyledFormWrapper = styled(FormWrapper)`
  width: 100%;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;

  .ant-form-item {
    width: 100%;
    margin-bottom: 0;
  }

  .ant-form-item-label {
    padding-bottom: 6px;
  }

  .ant-form-item-label > label {
    height: auto;
    font-family: Inter, sans-serif;
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
    color: #414651;
  }

  .ant-form-item-explain-error {
    margin-top: 4px;
    font-size: 13px;
  }

  input,
  textarea,
  .ant-input {
    width: 100%;
    min-height: 44px;
    padding: 10px 14px;
    background: #ffffff;
    border: 1px solid #d5d7da;
    border-radius: 8px;
    box-shadow: ${inputShadow};
    font-family: Inter, sans-serif;
    font-size: 16px;
    font-weight: 400;
    line-height: 24px;
    color: #181d27;
  }

  textarea {
    min-height: 140px;
  }
`;

const Actions = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
  /* Abstand nach oben kommt vom Upload-Baustein (margin-bottom: 24px) */
  margin-top: -16px;

  .actions-buttons {
    display: flex;
    gap: 12px;
  }

  .actions-buttons button {
    flex: 1;
    min-width: 0;
    margin-bottom: 0;
  }
`;

const RecipientsHint = styled.p`
  margin: 0;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #535862;
`;

const formatCount = (n) => n.toLocaleString('de-DE');

// Hinweis, wer per Mail benachrichtigt wird.
// recipientsCount: null = wird geladen, 'error' = konnte nicht geladen werden, sonst Zahl.
const recipientsHint = (recipientsCount, mailsLeftToday, mailsPerDay) => {
  if (mailsLeftToday === 0) {
    return `Heute wurden schon ${mailsPerDay} Updates per E-Mail verschickt. Dieses Update erscheint nur auf der Projektseite, ohne E-Mail.`;
  }
  if (recipientsCount === 0) {
    return 'Es wird keine E-Mail verschickt, weil bisher niemand gespendet oder das Projekt gespeichert hat.';
  }
  if (recipientsCount === 1) {
    return '1 Person wird per E-Mail über das Update benachrichtigt.';
  }
  if (Number.isInteger(recipientsCount)) {
    return `${formatCount(
      recipientsCount
    )} Unterstützer und Interessierte werden per E-Mail über das Update benachrichtigt.`;
  }
  return 'Alle Unterstützer und Interessierten werden per E-Mail über das Update benachrichtigt.';
};

export class UpdatesForm extends Component {
  static propTypes = {
    createProjectUpdates: PropTypes.func.isRequired,
    projectId: PropTypes.string.isRequired,
    handelCloseModal: PropTypes.func.isRequired,
    loading: PropTypes.bool.isRequired,
  };

  state = {
    image: [],
    uploadPercent: 0,
    recipientsCount: null,
    mailsLeftToday: null,
    mailsPerDay: null,
  };

  componentDidMount() {
    this.mounted = true;
    projectsServices
      .getUpdateRecipientsCount(this.props.projectId)
      .then((res) => {
        if (this.mounted) {
          this.setState({
            recipientsCount: Number.isInteger(res && res.count)
              ? res.count
              : 'error',
            mailsLeftToday: res ? res.mails_left_today : null,
            mailsPerDay: res ? res.mails_per_day : null,
          });
        }
      })
      .catch(() => {
        if (this.mounted) this.setState({ recipientsCount: 'error' });
      });
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  onFinish = (values) => {
    const { createProjectUpdates, projectId, handelCloseModal } = this.props;

    this.setState({ uploadPercent: 0 });

    const formData = new FormData();

    formData.append('update[title]', values.title);
    formData.append('update[body]', values.body);
    formData.append('update[project_id]', Number(projectId));

    const images = this.state.image || [];

    for (const im of images) {
      if (im && im.originFileObj) {
        formData.append('update[images][]', im.originFileObj, im.name);
      }
    }

    createProjectUpdates(
      formData,
      () => {
        this.setState({
          image: [],
          uploadPercent: 0,
        });

        handelCloseModal();
      },
      (percent) => {
        this.setState({ uploadPercent: percent });
      }
    );
  };

  render() {
    const { loading, handelCloseModal } = this.props;
    const {
      uploadPercent,
      recipientsCount,
      mailsLeftToday,
      mailsPerDay,
      image,
    } = this.state;

    return (
      <Form
        {...formLayout}
        name="project-update"
        onFinish={this.onFinish}
        validateMessages={formValidateMessages}
      >
        <StyledFormWrapper>
          <FormItem name="title" label="Titel" rules={[{ required: true }]}>
            <Input disabled={loading} placeholder="Worum geht es?" />
          </FormItem>

          <FormItem
            name="body"
            label="Update-Text"
            rules={[{ required: true }]}
          >
            <Input.TextArea
              rows={5}
              disabled={loading}
              placeholder="Erzähl deinen Unterstützern, was sich getan hat."
            />
          </FormItem>

          <UploadImageV2
            multiple={true}
            label={`Bilder (optional, bis zu ${MAX_IMAGES})`}
            name="image"
            width="150"
            height="100"
            images={image}
            setImage={(files) => {
              this.setState({ image: files.slice(0, MAX_IMAGES) });
            }}
          />

          <Actions>
            {loading && (
              <Progress
                percent={uploadPercent}
                status={uploadPercent >= 100 ? 'success' : 'active'}
                strokeColor="#9dbc60"
              />
            )}

            <RecipientsHint>
              {recipientsHint(recipientsCount, mailsLeftToday, mailsPerDay)}
            </RecipientsHint>

            <div className="actions-buttons">
              <PrimaryButton
                primary={true}
                type="button"
                onClick={handelCloseModal}
                disabled={loading}
              >
                Abbrechen
              </PrimaryButton>
              <PrimaryButton primary={false} type="submit" disabled={loading}>
                {loading ? 'Wird veröffentlicht …' : 'Update veröffentlichen'}
              </PrimaryButton>
            </div>
          </Actions>
        </StyledFormWrapper>
      </Form>
    );
  }
}

const mapStateToProps = (state) => {
  const { updates, loading, errors } = state.projects;

  return {
    updates,
    loading,
    errors,
  };
};

const mapDispatchToProps = (dispatch) => ({
  createProjectUpdates: (update, callback, onUploadProgress) =>
    dispatch(
      ProjectsActions.createProjectUpdates(update, callback, onUploadProgress)
    ),
});

export default connect(mapStateToProps, mapDispatchToProps)(UpdatesForm);
